import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MaskitoDirective } from '@maskito/angular';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCircleCheck, lucideLock, lucideTriangleAlert, lucideX } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import {
  EURO_AMOUNT_MASK,
  formatCurrency,
  formatCurrencyWithSymbol,
  parseEuroToCents,
} from '../../utils/currency.utils';

export interface RedemptionResult {
  redeemed: number;
  remaining: number;
}

/**
 * Redeeming a voucher, shared by /redeem and /verify: enter the amount, check it,
 * confirm. A redemption can't be undone, so nothing is booked before the review
 * step has been confirmed.
 *
 * The page does the API call: `confirmed` hands it the amount (null = the whole
 * balance), and it reports back through `busy`, `error` and `result`. Page-specific
 * fields (PIN, location on /verify) are projected into the entry step and gate it
 * through `ready`.
 */
@Component({
  selector: 'app-redemption-flow',
  standalone: true,
  imports: [
    FormsModule,
    MaskitoDirective,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputGroupImports,
    HlmSpinnerImports,
    HlmTypographyImports,
  ],
  providers: [provideIcons({ lucideCircleCheck, lucideLock, lucideTriangleAlert, lucideX })],
  templateUrl: './redemption-flow.component.html',
  styleUrls: ['./redemption-flow.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class RedemptionFlowComponent {
  readonly code = input.required<string>();
  /** Current balance in cents. */
  readonly balance = input.required<number>();
  /** Where the voucher is redeemed, shown in the review (network vouchers). */
  readonly location = input<string | null>(null);
  /** The projected fields are filled in. */
  readonly ready = input(true);
  readonly busy = input(false);
  readonly error = input('');
  readonly errorTitle = input('Einlösung fehlgeschlagen');
  /** Redeeming can't go on here (e.g. PIN locked): only the error is shown. */
  readonly locked = input(false);
  readonly result = input<RedemptionResult | null>(null);

  /** Amount in cents, or null for the whole balance. */
  readonly confirmed = output<number | null>();
  /** "Weiter einlösen" after a partial redemption. */
  readonly continued = output<void>();
  readonly errorDismissed = output<void>();

  protected readonly euroMask = EURO_AMOUNT_MASK;
  protected readonly money = formatCurrencyWithSymbol;

  protected readonly step = signal<'enter' | 'review'>('enter');
  /** Starts at the whole balance: most redemptions are full, a partial one is typed over it. */
  protected readonly amountText = linkedSignal(() => formatCurrency(this.balance()));
  protected readonly amountError = signal('');
  protected readonly amountCents = computed(() => parseEuroToCents(this.amountText()) ?? 0);
  protected readonly isFull = computed(() => this.amountCents() === this.balance());

  constructor() {
    // A failed confirm (wrong PIN, balance changed meanwhile) goes back to the
    // fields, so they can be corrected right where the error is shown.
    effect(() => {
      if (this.error()) this.step.set('enter');
    });
  }

  protected review(): void {
    if (!this.ready() || this.busy()) return;
    const cents = parseEuroToCents(this.amountText());
    if (cents === null || cents <= 0 || cents > this.balance()) {
      this.amountError.set(
        `Bitte einen Betrag zwischen 0,01 € und ${this.money(this.balance())} angeben.`,
      );
      return;
    }
    this.amountError.set('');
    this.errorDismissed.emit();
    this.step.set('review');
  }

  protected confirm(): void {
    if (this.busy()) return;
    this.confirmed.emit(this.isFull() ? null : this.amountCents());
  }

  protected continue(): void {
    this.step.set('enter');
    this.amountText.set(formatCurrency(this.balance()));
    this.continued.emit();
  }

  protected redeemAll(): void {
    this.amountText.set(formatCurrency(this.balance()));
    this.amountError.set('');
  }
}
